/**
 * Development Software List
 * 
 * This list contains software that is currently under development and not yet accessible to users.
 * These software will be displayed in the UI but marked as "in development" to indicate they are not yet available.
 * When a software is in this list:
 * - It will be displayed in the UI but marked as "in development"
 * - Users cannot create instances of these software yet
 * - The software will be marked with isDevelopment: true in the template list
 * - This helps users know which features are coming soon
 */
export const DevelopmentSoftwares = [
    'rabbitmq',
    'airflow',
    'flux',
    'grafana',
    'grafana-loki',
    'jenkins',
    'openwebui',
    'crewai',
    'ollama',
];

/**
 * Unlocked/Free Software List
 * 
 * This list contains software that is available to users without additional cost.
 * These are typically end-user applications and development tools that are free to use.
 * When a software is in this list:
 * - It will be marked as free in the UI
 * - It will be sorted to appear first in the template list
 * - Users can create instances of these without additional credits
 * - The software will be marked with isFree: true in the template list
 */
export const unlockedSoftwares = [
    'vscode',
    'jupyter-notebook',
    'streamlit',
    'vscode-python',
    'vscode-nodejs',
    'mongodb',
    'redis',
    'n8n',
    'mariadb',
    'neo4j',
    'autogen-studio',
    'langflow',
    'postgresql',
];

/**
 * All Available Software List
 * 
 * This is a comprehensive list of all software available in the system.
 * It combines:
 * - Software under development (from DevelopmentSoftwares)
 * - Free/Unlocked software (from unlockedSoftwares)
 * - Additional software that may be available but not in either category
 * 
 * Usage:
 * - Used to check if a software exists in the system
 * - Used for displaying all available options
 * - Used for filtering and searching
 * - Each software should appear exactly once in this list
 */
export const allSoftwares = [
    'vscode', 'jupyter-notebook', 'streamlit', // Essentials
    'vscode-python', 'vscode-nodejs', 'playground-jupyter', // IDEs
    'mongodb', 'postgresql', 'mariadb', 'redis', 'neo4j', 'elasticsearch', // Databases
    'chroma', 'qdrant', // Vector DBs
    'n8n', 'airflow', 'jenkins', // Automation
    'openwebui', 'ollama', 'crewai', 'autogen-studio', 'langflow', // AI/LLM/Agents
    'grafana', 'grafana-loki', // Analytics
    'rabbitmq', 'flux', // Other
];

// Section order and mapping
const sectionOrder = [
    ['Essentials', ['vscode', 'jupyter-notebook', 'streamlit']],
    ['IDEs', ['vscode-python', 'vscode-nodejs', 'playground-jupyter']],
    ['Databases', ['mongodb', 'postgresql', 'mariadb', 'redis', 'neo4j', 'elasticsearch']],
    ['Vector Databases', ['chroma', 'qdrant']],
    ['Automation & Orchestration', ['n8n', 'airflow', 'jenkins']],
    ['AI/LLM/Agents', ['openwebui', 'ollama', 'crewai', 'autogen-studio', 'langflow']],
    ['Analytics & Visualization', ['grafana', 'grafana-loki']],
    ['Other', ['rabbitmq', 'flux']]
];

const assigned = new Set();
export const softwareSections = {};

// Assign only non-development softwares to main sections
for (const [section, keys] of sectionOrder) {
    softwareSections[section] = keys.filter(
        key => !DevelopmentSoftwares.includes(key) && !assigned.has(key) && assigned.add(key)
    );
}

// Add a separate section for development/coming soon
softwareSections['Coming Soon'] = DevelopmentSoftwares.filter(
    key => allSoftwares.includes(key) && !assigned.has(key) && assigned.add(key)
);